import { CustomerContacts, OrderTemplate } from "@/types";
import { useCallback, useEffect, useRef, useState } from "react";

import { ApiCustomerAddress } from "@/api/customer/interface.api";
import { BaseApiUser } from "@/api/user/interface.api";
import { CustomerDetails } from "#/pages";
import { useApi } from "@/api";
import { useNotes } from "@/hooks";
import { useParams } from 'react-router-dom';

const CustomerDetailsView = () => {
  const { account, customer, orderTemplate, orderType, user, session } = useApi();
  const { id } = useParams<'id'>();
  const { cbId } = useParams<'cbId'>();
  const customerId = id ?? session.user?.id ?? '';
  const isAdmin = session.user?.admin ?? false;

  const [loadingOrderTemplates, setLoadingOrderTemplates] = useState(false);
  const branchId = useRef(0);

  const { notes, onAddNote, setNotes } = useNotes();

  const getOrderTemplates = async () => {
    setLoadingOrderTemplates(true);
    const branches = await customer.getBranches(customerId);

    if (cbId)
    {
      branchId.current = Number(cbId);
    }
    else
    {
      branchId.current = branches[0].BranchId;
    }
    
    orderTemplate.getList(branches);
    setLoadingOrderTemplates(false);
  }

  useEffect(() => {
    getOrderTemplates();
  }, []);


  const handleSaveAccountDetails = useCallback(async (data: BaseApiUser) => {
    user.updateUser(data);
  }, []);

  const handleChangePassword = useCallback(async (oldPassword: string, newPassword: string, confirmPassword: string ) => {
    account.changePassword(customerId, oldPassword, newPassword, confirmPassword);
  }, [customerId]);

  const handleSaveDetails = useCallback(async (customerIdInput:string, data: ApiCustomerAddress) => {
    customer.updateDetails(data, customerIdInput, branchId.current.toString());
  }, [customerId, branchId]);

 
  const getDataAndLogo  = async () => {
    await customer.getData(null, customerId);
    await customer.getLogo(customerId);

    if (isAdmin)
    {
      customer.getNotes(customerId)
        .then((n) => {
          setNotes(n);
        });
    }
  }

  const handleLogoUpload = async (data: Blob) => {
    await customer.uploadLogo(customerId, data);
    await getDataAndLogo();
  }

  const handleAddOrderTemplate = useCallback(async (orderTemplateInput: OrderTemplate) => {
    await orderTemplate.set(orderTemplateInput);
    await getOrderTemplates();
  }, [])

  const hadleGetBlankServices = useCallback(async () => {
    return await orderType.getEditList(customerId);
  }, [customerId]);

  const handleRemoveContact = useCallback(async (contactId: string) => {
    await customer.removeContact(customerId, branchId.current, Number(contactId));
    await getDataAndLogo();
  }, []);

  const handleAddContact = useCallback(async (contact: CustomerContacts) => {
    await customer.newContact({
     BranchId: branchId.current,
     ContactId: 0,
     CustomerId: customerId,
     EmailAddress: contact.email,
     Name: contact.name,
     PhoneNumber1: contact.phone1,
     PhoneNumber2: contact.phone2,
     SendInvoice: contact.sendInvoice
    });
    await getDataAndLogo();
  }, [branchId.current, customerId]);

  const handleEditContact = useCallback(async (contact: CustomerContacts) => {
    await customer.editContact({
     BranchId: branchId.current,
     ContactId: Number(contact.contactId),
     CustomerId: customerId,
     EmailAddress: contact.email,
     Name: contact.name,
     PhoneNumber1: contact.phone1,
     PhoneNumber2: contact.phone2,
     SendInvoice: contact.sendInvoice
    });
    await getDataAndLogo();
  }, [branchId.current, customerId]);

  const handleCancelNote = async () => {
    setNotes(notes.slice(0, -1));
  };

  const handleSaveNote = useCallback(async (noteContent: string) => {
    const lastNote = notes[notes.length - 1];
    lastNote.content = noteContent;
    lastNote.created = new Date().toISOString();
    customer.addNote(branchId.current, noteContent);
    setNotes([...notes.slice(0, -1), lastNote]);
  }, [branchId.current, notes]);

  useEffect(() => {
    getDataAndLogo();
  }, []);

  return (
    <>
      { customerId && customer.customerData && orderTemplate.orderTemplates && <CustomerDetails
        branchId={branchId.current}
        customerId={customerId}
        customerData={customer.customerData}
        getBlankServices={hadleGetBlankServices}
        isAdmin={isAdmin}
        loadingOrderTemplates={loadingOrderTemplates}
        logoUrl={customer.logoUrl}
        notes={notes}
        onAddNote={onAddNote}
        onCancelNote={handleCancelNote}
        onSaveNote={handleSaveNote}
        onAddOrderTemplate={handleAddOrderTemplate}
        onEditContact={handleEditContact}
        onRemoveContact={handleRemoveContact}
        onLogoUpload={handleLogoUpload}
        onAddNewContact={handleAddContact}
        onChangePassword={handleChangePassword}
        onSaveAccountDetails={handleSaveAccountDetails}
        onSaveDetails={handleSaveDetails}
        orderTemplates={orderTemplate.orderTemplates}
        owner={`${session.user?.firstName} ${session.user?.lastName}`} /> }
    </>
  );
}

export default CustomerDetailsView;